import {
  assign,
  get,
  isEmpty,
  first,
  values,
  size,
  sumBy,
  uniqBy,
  includes,
} from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import queryString from 'querystring';
import { Button, ButtonGroup } from '@mui/material';

import BookingsList from './BookingsList';
import DeliveryModal from './DeliveryModal';
import LocationSelector from './LocationSelector';
import RemoveBookingModal from './RemoveBookingModal';

import BookingTypes from '../../Lib/Constants/booking-types';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import historyProps from '../../Lib/Utils/PropTypes/history-props';
import DateSwitcher from '../ReusableComponents/DateSwitcher/DateSwitcher';
import LinearProgress from '../ReusableComponents/LinearProgress';
import styles from './Deliveries.module.css';
import BlockModal from './BlockModal';
import ViewBookingModal from './ViewBookingModal';
import ExportDeliveriesModal from './ExportDeliveriesModal';
import QuickOrderSearchModal from './QuickOrderSearchModal';
import Role, { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';
import QuickBookingSearchModal from './Modals/ReferenceSearchModal/QuickBookingSearchModal.tsx';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';

const formatDate = (date) => {
  const format = 'MM-DD-YYYY';
  return date ? moment.utc(date, format) : moment.utc();
};

const calculateTotalBoxesRemaining = (deliveriesArray) => {
  const remainingBoxes =
    sumBy(deliveriesArray, (d) => +d.boxes_received) -
    sumBy(deliveriesArray, 'boxes');
  return `${remainingBoxes > 0 ? '+' : ''}${Math.abs(remainingBoxes)}`;
};

function Deliveries(props) {
  const {
    location,
    history,
    getLocations,
    getDeliveries,
    availabilities,
    blocks,
    currentUser,
    deliveries,
    fetchVendors,
    getAvailabilities,
    getDeliverablePurchaseOrders,
    deliverablePurchaseOrders,
    createBlock,
    updateBlock,
    createDelivery,
    updateDelivery,
    removeBlock,
    removeDelivery,
    locations,
    loading,
    vendors,
    checkInDelivery,
  } = props;
  const { search: queryParams, pathname } = location;

  const params = useMemo(() => decodeLocation(queryParams), [queryParams]);

  const [state, setState] = React.useState({
    openBlockModal: false,
    openExportModal: false,
    openDeliveryModal: false,
    openDetailsModal: false,
    openPOSearch: false,
    openRefSearch: false,
    openRemoveModal: false,
    selectedBlock: null,
    selectedDelivery: null,
  });

  useEffect(() => {
    getLocations();
  }, [getLocations]);

  const fetchDeliveries = useCallback(
    (locationId, date) => {
      const formattedDate = formatDate(date).format('MM-DD-YYYY');
      const options = {
        location_id: locationId,
        type: [BookingTypes.ORDER_DELIVERY, BookingTypes.BLOCK],
        date: moment.utc(formattedDate, 'MM-DD-YYYY').startOf('day').format(),
      };
      getDeliveries(options);
    },
    [getDeliveries],
  );

  useEffect(() => {
    if (params.location_id) {
      fetchDeliveries(params.location_id, params.date);
    }
  }, [params.location_id, params.date, fetchDeliveries]);

  const toggleBlockModal = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      openBlockModal: !prevState.openBlockModal,
      selectedBlock: prevState.openBlockModal ? null : prevState.selectedBlock,
    }));
  }, []);

  const toggleExportModal = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      openExportModal: !prevState.openExportModal,
    }));
  }, []);

  const toggleDeliveryModal = useCallback(
    (refreshData = false) => {
      setState((prevState) => ({
        ...prevState,
        openDeliveryModal: !prevState.openDeliveryModal,
        selectedDelivery: prevState.openDeliveryModal
          ? null
          : prevState.selectedDelivery,
      }));
      if (refreshData === true) {
        fetchDeliveries(params.location_id, params.date);
      }
    },
    [params.location_id, params.date, fetchDeliveries],
  );

  const toggleDetails = useCallback((booking) => {
    const isBlock = booking.type === BookingTypes.BLOCK;
    const key = isBlock ? 'selectedBlock' : 'selectedDelivery';
    setState((prevState) => ({
      ...prevState,
      openDetailsModal: !prevState.openDetailsModal,
      [key]: prevState.openDetailsModal ? null : booking,
    }));
  }, []);

  const togglePOSearch = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      openPOSearch: !prevState.openPOSearch,
    }));
  }, []);

  const toggleRefSearch = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      openRefSearch: !prevState.openRefSearch,
    }));
  }, []);

  const toggleRemoveModal = useCallback((booking) => {
    const isBlock = booking.type === BookingTypes.BLOCK;
    const key = isBlock ? 'selectedBlock' : 'selectedDelivery';
    setState((prevState) => ({
      ...prevState,
      openRemoveModal: !prevState.openRemoveModal,
      [key]: prevState.openRemoveModal ? null : booking,
    }));
  }, []);

  const updateLocationAndDate = useCallback(
    (locationId, date) => {
      if (locationId === params.location_id && date === params.date) return;
      const searchToEncode = {};
      if (locationId) {
        searchToEncode.location_id = locationId;
        setState((prevState) => ({
          ...prevState,
          selectedDelivery: {
            ...prevState.selectedDelivery,
            location_id: locationId,
          },
        }));
      }
      if (date) searchToEncode.date = date;
      const search = queryString.encode(searchToEncode);

      history.push({ pathname, search });
    },
    [params.location_id, params.date, pathname, history],
  );

  const updateLocation = useCallback(
    (locationId) => {
      updateLocationAndDate(locationId, params.date);
    },
    [updateLocationAndDate, params.date],
  );

  const updateDate = useCallback(
    (date) => {
      updateLocationAndDate(params.location_id, date.format('MM-DD-YYYY'));
    },
    [updateLocationAndDate, params.location_id],
  );

  const editBooking = useCallback(
    (booking) => {
      const isBlock = booking.type === BookingTypes.BLOCK;
      const key = isBlock ? 'selectedBlock' : 'selectedDelivery';
      setState((prevState) => ({
        ...prevState,
        [key]: booking,
      }));
      if (isBlock) {
        toggleBlockModal();
      } else {
        toggleDeliveryModal();
      }
    },
    [toggleBlockModal, toggleDeliveryModal],
  );

  const removeBooking = useCallback(
    (booking, reason) => {
      if (booking.type === BookingTypes.ORDER_DELIVERY) {
        removeDelivery(booking.id, reason);
      }

      if (booking.type === BookingTypes.BLOCK) {
        removeBlock(booking.id, reason);
      }

      toggleRemoveModal(booking);
    },
    [removeBlock, removeDelivery, toggleRemoveModal],
  );

  const saveDeliveryV2 = useCallback(
    (delivery, purchaseOrderNumbers) => {
      const cb = () => {
        updateDate(moment.utc(delivery.starts_at).startOf('day'));
        toggleDeliveryModal();
      };
      if (delivery.id) {
        updateDelivery(delivery.id, delivery, purchaseOrderNumbers, cb);
      } else {
        createDelivery(
          { ...delivery, id: undefined }, // If we don't remove the id, the API will return a 422
          purchaseOrderNumbers,
          cb,
        );
      }
    },
    [
      createDelivery,
      updateDelivery,
      updateDate,
      toggleDeliveryModal,
      params.location_id,
      params.date,
    ],
  );

  const saveBlock = useCallback(
    (block) => {
      const cb = () => {
        toggleBlockModal();
        updateLocationAndDate(
          block.location_id,
          moment.utc(block.starts_at).startOf('day'),
        );
      };
      if (block.id) {
        updateBlock(block.id, block, cb);
      } else {
        createBlock({ ...block, type: BookingTypes.BLOCK }, cb);
      }
    },
    [createBlock, updateBlock, toggleBlockModal, updateLocationAndDate],
  );

  const perms = useMemo(() => {
    const canAddBlock = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.CREATE,
      'BLOCK',
    );
    const canCreateDelivery = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.CREATE,
      'DELIVERY',
    );
    const canExport = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.READ,
      'DELIVERY',
    );
    const canReadPo = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.READ,
      'PO',
    );
    const canReadRef = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.READ,
      'REF',
    );
    const canUpdatePackingList = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.UPDATE,
      'PACKING_LIST',
    );
    const canReadPackingList = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.READ,
      'PACKING_LIST',
    );
    return {
      canAddBlock,
      canCreateDelivery,
      canExport,
      canReadPo,
      canReadRef,
      canUpdatePackingList,
      canReadPackingList,
    };
  }, [currentUser]);

  const rowPerms = useMemo(() => {
    const canUpdateDelivery = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.UPDATE,
      'DELIVERY',
    );
    const canDeleteDelivery = hasPermissionTo(
      'deliveries',
      currentUser.role,
      Actions.DELETE,
      'DELIVERY',
    );
    return {
      canUpdateDelivery,
      canDeleteDelivery,
    };
  }, [currentUser]);

  if (isEmpty(locations)) return null;

  const {
    selectedDelivery,
    selectedBlock,
    openRemoveModal,
    openBlockModal,
    openDeliveryModal,
    openDetailsModal,
    openExportModal,
    openPOSearch,
    openRefSearch,
  } = state;
  const { location_id: locationId } = params;
  const date = formatDate(params.date);
  const selectedLocation = locations[locationId] || first(values(locations));
  const deliveriesArray = values(deliveries);
  const isUpdateRole = includes(
    [Role.SCHEDULER, Role.ADMIN, Role.COMPLIANCE],
    get(currentUser, 'role'),
  );

  return (
    <Page.Container withoutTwp>
      <Header.Major icon="truck/truck-trash" title="Deliveries" />
      <Page.Content>
        <LinearProgress loading={loading} />
        <div className={styles.containerHeader}>
          <div className={styles.containerBar}>
            <div className={styles.dailyTotals}>
              Total Vendors:
              <span>{size(uniqBy(deliveriesArray, 'vendor.id'))}</span>
            </div>
            <div className={styles.dailyTotals}>
              Total Purchase Orders:
              <span>
                {` ${sumBy(deliveriesArray, (d) => size(d.purchase_orders))}`}
              </span>
            </div>
            <div className={styles.dailyTotals}>
              Total Boxes:
              <span>{` ${sumBy(deliveriesArray, 'boxes')}`}</span>
            </div>
            <div className={styles.dailyTotals}>
              Total Remaining Boxes:
              <span>{calculateTotalBoxesRemaining(deliveriesArray)}</span>
            </div>
            <div className={styles.dailyTotals}>
              Total Units:
              <span>{` ${sumBy(deliveriesArray, 'units')}`}</span>
            </div>
          </div>

          <div className={styles.containerBar}>
            <LocationSelector
              selectedLocation={selectedLocation}
              onChange={updateLocation}
              locations={locations}
            />

            <div className={styles.actions}>
              <ButtonGroup color="primary">
                {perms.canCreateDelivery && (
                  <Button onClick={toggleDeliveryModal}>
                    Schedule Delivery
                  </Button>
                )}
                {perms.canAddBlock && (
                  <Button onClick={toggleBlockModal}>Add Block</Button>
                )}

                {perms.canExport && (
                  <Button onClick={toggleExportModal}>Export</Button>
                )}
                {perms.canReadPo && (
                  <Button onClick={togglePOSearch}>PO Search</Button>
                )}
                {perms.canReadRef && (
                  <Button onClick={toggleRefSearch}>Ref Search</Button>
                )}
              </ButtonGroup>
            </div>

            <DateSwitcher date={moment.utc(date)} onDateChange={updateDate} />
          </div>
        </div>

        <div className={styles.containerBody}>
          <BookingsList
            removeBooking={toggleRemoveModal}
            editBooking={editBooking}
            saveDelivery={updateDelivery}
            checkInDelivery={checkInDelivery}
            bookings={assign(deliveries, blocks)}
            toggleDetails={toggleDetails}
            updateRole={isUpdateRole}
            currentUser={currentUser}
            rowPerms={rowPerms}
            pagePerms={perms}
          />
        </div>

        <RemoveBookingModal
          booking={selectedDelivery || selectedBlock || {}}
          open={openRemoveModal}
          locations={locations}
          onClose={toggleRemoveModal}
          onRemove={removeBooking}
        />

        <DeliveryModal
          availabilities={availabilities}
          defaultDate={moment.utc(date)}
          defaultLocation={selectedLocation}
          delivery={selectedDelivery || {}}
          deliveries={deliveries}
          getDeliveries={getDeliveries}
          deliverablePurchaseOrders={deliverablePurchaseOrders}
          fetchVendors={fetchVendors}
          getAvailabilities={getAvailabilities}
          getDeliverablePurchaseOrders={getDeliverablePurchaseOrders}
          loading={loading}
          locations={locations}
          onClose={toggleDeliveryModal}
          open={openDeliveryModal}
          saveDelivery={saveDeliveryV2}
          superuser
          vendor={get(selectedDelivery, 'vendor')}
          vendors={vendors}
        />

        <BlockModal
          block={selectedBlock || {}}
          locations={locations}
          onClose={toggleBlockModal}
          open={openBlockModal}
          saveBlock={saveBlock}
        />

        <ViewBookingModal
          booking={selectedBlock || selectedDelivery || {}}
          onClose={toggleDetails}
          open={openDetailsModal}
          locations={locations}
        />

        {openExportModal && (
          <ExportDeliveriesModal
            locations={locations}
            defaultDate={moment.utc(date).format('YYYY-MM-DD')}
            onClose={toggleExportModal}
          />
        )}

        {currentUser && (
          <>
            <QuickOrderSearchModal
              open={openPOSearch}
              onClose={togglePOSearch}
              currentUser={currentUser}
              perms={perms}
            />
            <QuickBookingSearchModal
              open={openRefSearch}
              onClose={toggleRefSearch}
            />
          </>
        )}
      </Page.Content>
    </Page.Container>
  );
}

Deliveries.propTypes = {
  availabilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  blocks: PropTypes.shape({}).isRequired,
  createBlock: PropTypes.func.isRequired,
  createDelivery: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({}).isRequired,
  deliverablePurchaseOrders: PropTypes.shape({}).isRequired,
  deliveries: PropTypes.shape({}).isRequired,
  fetchVendors: PropTypes.func.isRequired,
  getDeliveries: PropTypes.func.isRequired,
  getLocations: PropTypes.func.isRequired,
  getAvailabilities: PropTypes.func.isRequired,
  getDeliverablePurchaseOrders: PropTypes.func.isRequired,
  history: historyProps.isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.shape({}).isRequired,
  locations: PropTypes.shape({}).isRequired,
  removeBlock: PropTypes.func.isRequired,
  removeDelivery: PropTypes.func.isRequired,
  updateDelivery: PropTypes.func.isRequired,
  checkInDelivery: PropTypes.func.isRequired,
  updateBlock: PropTypes.func.isRequired,
  vendors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default Deliveries;
